import React, { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import PartnerClientBondOrderInfo from "./PartnerClientBondOrderInfo";
import { makeGETRequestOnUserService } from "../../Api/api";
import {
  USER_TYPE_BOND_ORDER_HISTORY_ENDPOINTS,
  BondOrderHistoryResponse,
  dedupeBondOrders,
  getBondOrderSide,
  normalizeBondOrdersPayload,
} from "../Users/Orders/helpers";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import toastr from "toastr";
import axios from "axios";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import { userServiceAxiosInstance } from "../../Api/setUserServiceToken";
import { getOrderTimestamp } from "../../Helpers/functions";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";

type OrderSideFilter = "All" | "Buy" | "Sell";
const ORDER_SIDE_FILTERS: OrderSideFilter[] = ["All", "Buy", "Sell"];

const PartnerClientBondOrders = ({currentUser, user_type }: any) => {
  const [collapseIndex, setCollapseIndex] = useState(-1);
  const [bondOrders, setBondOrders] = useState<BondOrderHistoryResponse[]>([]);
  const [isLoadingBondOrders, setIsLoadingBondOrders] = useState(false);
  const [orderSideFilter, setOrderSideFilter] = useState<OrderSideFilter>(
    "All"
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [uuid, setUUID] = useState("");
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState<File | null>(null);

  useEffect(() => {
    let mounted = true;
    const fetchBondOrders = async () => {
      setIsLoadingBondOrders(true);
      try {
        const responses = await Promise.allSettled(
          USER_TYPE_BOND_ORDER_HISTORY_ENDPOINTS.map((buildUrl) =>
            makeGETRequestOnUserService(buildUrl(currentUser.uuid, user_type))
          )
        );

        const fulfilledResponses = responses.filter(
          (
            response
          ): response is PromiseFulfilledResult<{ data?: unknown }> =>
            response.status === "fulfilled"
        );

        if (!fulfilledResponses.length) {
          throw new Error("Failed to fetch bond orders");
        }

        const combinedOrders = dedupeBondOrders(
          fulfilledResponses.flatMap((response) =>
            normalizeBondOrdersPayload(response.value.data)
          )
        );

        if (mounted) {
          setBondOrders(combinedOrders);
        }
      } catch (error) {
        if (mounted) {
          toastr.error("Failed to fetch bond orders");
        }
      } finally {
        if (mounted) {
          setIsLoadingBondOrders(false);
        }
      }
    };
    if (currentUser?.uuid) {
      fetchBondOrders();
    }
    return () => {
      mounted = false;
    };
  }, [currentUser?.uuid]);

  const uploadFundTransfer = async (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();
    if (!file) {
      toastr.error("Please select a file to upload.");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("uuid", uuid);

    setUploading(true);
    try {
      const response = await userServiceAxiosInstance.post(
        `order_management_v2/fund_transfer_api/?type=${user_type}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      if (response.status === 200) {
        toastr.success("File Uploaded Successfully.");
        handleCloseDialog();
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        toastr.error(
          `${error.response?.status} : ${JSON.stringify(error.response?.data)}`
        );
      }
    } finally {
      setUploading(false);
    }
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setUUID("");
    setFile(null);
  };

  const filteredAndSortedOrders = useMemo(() => {
    if (!bondOrders?.length) {
      return [];
    }

    const normalizedSearch = searchQuery.trim().toLowerCase();

    const filtered = bondOrders.filter((order) => {
      const matchesSide =
        orderSideFilter === "All" ||
        getBondOrderSide(order.bid_offer) === orderSideFilter;

      if (!matchesSide) {
        return false;
      }

      if (!normalizedSearch) {
        return true;
      }

      const haystack = `${order.isin ?? ""} ${
        order.security_name ?? ""
      } ${order.deal_status ?? ""}`.toLowerCase();

      return haystack.includes(normalizedSearch);
    });

    return filtered.sort(
      (a, b) => getOrderTimestamp(b) - getOrderTimestamp(a)
    );
  }, [bondOrders, orderSideFilter, searchQuery]);

  const renderEmptyState = (message: string) => (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        my: 2,
      }}
    >
      <Typography
        sx={{
          p: 2,
          bgcolor: "#0000001A",
          borderRadius: "12px",
          color: "#9397A4",
        }}
      >
        {message}
      </Typography>
    </Box>
  );

  return (
    <>
      <Box sx={{ pb: 2 }}>
        {isLoadingBondOrders ? (
          <CircularProgress color="error" />
        ) : bondOrders?.length ? (
          <>
            <Box
              sx={{
                display: "flex",
                flexDirection: ["column", "row"],
                alignItems: ["stretch", "center"],
                justifyContent: "flex-start",
                flexWrap: "wrap",
                gap: 2,
                mb: 2,
              }}
            >
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {ORDER_SIDE_FILTERS.map((option) => (
                  <Button
                    key={option}
                    variant={
                      orderSideFilter === option ? "contained" : "outlined"
                    }
                    onClick={() => setOrderSideFilter(option)}
                    size="small"
                    sx={{ fontWeight: 600 }}
                  >
                    {option}
                  </Button>
                ))}
              </Box>
              <TextField
                size="small"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Search ISIN / Name / Deal Status"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRoundedIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: ["100%", 320] }}
              />
            </Box>
            {filteredAndSortedOrders?.length ? (
              filteredAndSortedOrders.map((item, index) => (
                <PartnerClientBondOrderInfo
                  key={index}
                  index={index}
                  collapseIndex={collapseIndex}
                  item={item}
                  setCollapseIndex={setCollapseIndex}
                  setOpen={setOpen}
                  setUUID={setUUID}
                  user_type={user_type}
                />
              ))
            ) : (
              renderEmptyState("No Bond Orders match your filters.")
            )}
          </>
        ) : (
          renderEmptyState("No Bond Orders Found")
        )}
      </Box>
      <Dialog open={open} onClose={handleCloseDialog} fullWidth>
        <DialogTitle
          sx={{ fontWeight: 600, fontSize: 15, color: "#FFFFFF", px: [2, 3] }}
        >
          Please Upload Fund Transfer Proof
        </DialogTitle>
        <DialogContent>
          <Box
            component="form"
            noValidate
            sx={{ width: ["100%"] }}
            onSubmit={uploadFundTransfer}
          >
            <input
              type="file"
              name="file"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              required
            />
            <Button
              variant="contained"
              type="submit"
              disabled={uploading}
              sx={{ mt: 1 }}
            >
              {uploading ? "Uploading..." : "Submit"}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PartnerClientBondOrders;
